import { faInfoCircle } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Flex, useMantineTheme } from '@mantine/core';
import { observer } from 'mobx-react-lite';
import numeral from 'numeral';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import {
  AsyncDisplay,
  DetailedProgressBar,
  Skeleton,
  Text,
  Tooltip,
  Well,
} from '@feathr/components';
import { useLimitColorFeedback } from '@feathr/extender/hooks/useLimitColorFeedback';
import { useStatusIcon } from '@feathr/extender/hooks/useStatusIcon';
import useUsageLimits from '@feathr/extender/state/useUsageLimits';

import * as styles from './FeatureUsageLimit.css';

type TFeatureUsageType = 'contacts' | 'emails';

interface IFeatureUsageLimitProps {
  type: TFeatureUsageType;
  used: number;
  title?: string;
  tooltip?: string;
  limit?: number;
  loading?: boolean;
}

function FeatureUsageLimit({
  type,
  used,
  title,
  tooltip,
  limit,
  loading = false,
}: Readonly<IFeatureUsageLimitProps>): JSX.Element | null {
  const theme = useMantineTheme();
  const { t } = useTranslation();
  const { personsLimit, emailSendLimit } = useUsageLimits();

  const effectiveLimit = limit ?? (type === 'contacts' ? personsLimit : emailSendLimit);

  const label = title ?? (type === 'contacts' ? t('Known People') : t('Email Usage'));
  const tooltipText =
    tooltip ??
    (type === 'contacts'
      ? t('Known People are person records with email addresses. Limit is based on your license.')
      : t('This shows your current email usage as a percentage of your monthly allocation'));

  const { progressTheme, progressShade, summaryColor } = useLimitColorFeedback(
    used,
    effectiveLimit || 0,
  );
  const { icon, iconColor } = useStatusIcon(used, effectiveLimit || 0);

  return (
    <AsyncDisplay isLoading={loading} skeleton={<Skeleton paragraphs={2} width={'100%'} />}>
      <Well className={styles.featureUsageLimit}>
        <DetailedProgressBar
          endValue={effectiveLimit}
          icon={icon}
          iconColor={iconColor}
          label={
            <Flex align={'center'} gap={theme.spacing['2']}>
              <Text.DefaultMedium>{label}</Text.DefaultMedium>
              <Tooltip position={'top'} title={tooltipText}>
                <FontAwesomeIcon icon={faInfoCircle} />
              </Tooltip>
            </Flex>
          }
          shade={progressShade}
          startValue={used}
          summary={
            <Text.LargeMedium c={summaryColor} mantineComponent={'span'}>
              {`${numeral(used).format('0,0')} / ${numeral(effectiveLimit).format('0,0')}`}
            </Text.LargeMedium>
          }
          summaryColor={summaryColor}
          theme={progressTheme}
        />
      </Well>
    </AsyncDisplay>
  );
}

export default observer(FeatureUsageLimit);
